import _IconButton from "@mui/material/IconButton";
import _Close from "@mui/icons-material/Close";
import _Typography from "@mui/material/Typography";
import _Box from "@mui/material/Box";
import { cellToLatLng } from 'h3-js';
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { gamePMTilesSourceId, useGamePMTiles, useGeoveloMap, useSource } from '../../hooks/map';
import useGameQuery from '../../hooks/use-game-query';
import useGameTeamsQuery from '../../hooks/use-game-teams-query';
import { AppService } from '../../services';
import { getDataFromUrl } from '../../utils';
import { bfgMinZoom, gameStyleDir, getBfgInitialCamera } from './exploration-map-game';
import { bfgIntergroupMineColor, bfgIntergroupPmtilesLayerId, bfgIntergroupTeamCircleFillColorExpression, bfgIntergroupTeamCircleRadiusExpression, bfgIntergroupTeamCircleStrokeColorExpression, bfgIntergroupTeamCircleStrokeWidthExpression, bfgIntergroupTeamIconCanvasSize, bfgIntergroupTeamIconImageExpression, bfgIntergroupTeamIconSizeExpression, buildBfgIntergroupBorderColorExpression, buildBfgIntergroupBorderWidthExpression, buildBfgIntergroupFillColorExpression, buildBfgIntergroupFillOpacityExpression, computeBfgIntergroupFeatureState, getBfgAllTeamValues, getBfgIntergroupTeamColor, getBfgLeader } from './exploration-map-game-intergroup-style';
const containerIdPrefix = 'event-exploration-map-game-intergroup';
const bfgTeamPointsSourceId = `${bfgIntergroupPmtilesLayerId}-team-points`;
const bfgTeamCircleLayerId = `${bfgIntergroupPmtilesLayerId}-team-circles`;
const bfgTeamIconLayerId = `${bfgIntergroupPmtilesLayerId}-team-icons`;
// Fixed — this map only ever shows the "all trips" ranking, unlike the solo map which lets the
// player switch to a commuting-only view.
const bfgIntergroupColorMode = 'score';
function teamIconId(teamId) {
  return `bfg-intergroup-team-icon-${teamId}`;
}

// Team icon uploads come in arbitrary sizes/aspect ratios — this crops/scales the source image to
// cover a square (like CSS `object-fit: cover`) and clips it to a circle, the same round-avatar
// treatment used everywhere else in the app (GeogroupAvatar, partner icons, …), instead of leaving
// it as whatever rectangular shape the upload happens to be.
function createCircularIconImageData(source, naturalWidth, naturalHeight, size) {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
  ctx.clip();
  const scale = Math.max(size / naturalWidth, size / naturalHeight);
  const drawWidth = naturalWidth * scale;
  const drawHeight = naturalHeight * scale;
  ctx.drawImage(source, (size - drawWidth) / 2, (size - drawHeight) / 2, drawWidth, drawHeight);
  return ctx.getImageData(0, 0, size, size);
}

// Loads a team's icon (skipped for teams without one), pre-processed into a round badge — see
// createCircularIconImageData — so every loaded icon ends up the same fixed square size
// (bfgIntergroupTeamIconCanvasSize) regardless of the original upload's dimensions.
async function loadTeamIcon(map, team) {
  if (!team.icon) return;
  const id = teamIconId(team.id);
  if (map.hasImage(id)) return;
  try {
    const image = await map.loadImage(team.icon);
    if (!map.hasImage(id)) {
      map.addImage(id, createCircularIconImageData(image.data, image.data.width, image.data.height, bfgIntergroupTeamIconCanvasSize));
    }
  } catch (err) {
    console.error('[ExplorationMapGameIntergroup] team icon', team.id, err);
  }
}
// Full ranking of every team's passages on a clicked hex (not just the leader) — feeds the right
// panel, not a map tooltip (see the selectedHexProperties state below).
function getBfgIntergroupRanking(properties, teams, colorMode) {
  const valueByTeamId = getBfgAllTeamValues(properties, colorMode);
  return teams.map(team => ({
    team,
    value: Number(valueByTeamId[team.id]) || 0
  })).sort((a, b) => b.value - a.value);
}
export function ExplorationMapGameIntergroup({
  gameId,
  headquartersGeogroup,
  mySiteGeogroup,
  ownGeogroupId,
  sites
}) {
  const containerId = `${containerIdPrefix}-${useId()}`;
  const bleedRootRef = useRef(null);
  const [bleedMargins, setBleedMargins] = useState(null);
  const {
    data: game
  } = useGameQuery(gameId, '{id title theme mode covered_zone start_datetime end_datetime pmtiles_file}');
  const colorMode = bfgIntergroupColorMode;
  const {
    map,
    initialized: baseMapInitialized,
    init: initMap
  } = useGeoveloMap({});
  const [customStyleLoaded, setCustomStyleLoaded] = useState(false);
  useEffect(() => {
    if (!map || !baseMapInitialized) return;
    let cancelled = false;
    setCustomStyleLoaded(false);
    getDataFromUrl(`${AppService.environment.tileServer.url}/styles/${gameStyleDir}/style.json`).then(({
      version,
      sources,
      layers,
      glyphs,
      sprite
    }) => {
      if (cancelled) return;

      // `setStyle` fully replaces the map's style (wiping any source/layer another hook may have
      // already added against the previous one) and loads asynchronously — every other effect in
      // this component keys off `initialized` below, which only flips once this settles, so
      // nothing tries adding hex layers before the custom style is actually in place.
      map.once('styledata', () => {
        if (!cancelled) setCustomStyleLoaded(true);
      });
      map.setStyle({
        version: version || 8,
        sources,
        layers,
        glyphs,
        sprite
      });
    });
    return () => {
      cancelled = true;
    };
  }, [map, baseMapInitialized]);
  // Shadows the hook's own `initialized` — every effect below assumes it means "the map's final
  // style (hex layers, custom background, …) is actually in place", which now also requires the
  // custom style fetch above to have completed, not just the initial default-style map creation.
  const initialized = baseMapInitialized && customStyleLoaded;
  const usesPMTiles = Boolean(game?.pmTilesFileUrl);
  const {
    data: bfgTeams
  } = useGameTeamsQuery(usesPMTiles ? gameId : undefined, {
    query: '{id title color icon challenge {id group}}'
  });
  const {
    data: myTeamResult
  } = useGameTeamsQuery(usesPMTiles ? gameId : undefined, {
    groupIds: ownGeogroupId ? [ownGeogroupId] : undefined
  });
  const myTeamId = usesPMTiles && ownGeogroupId ? myTeamResult?.[0]?.id ?? null : null;
  const computeFeatureState = useCallback(properties => computeBfgIntergroupFeatureState(properties, colorMode), [colorMode]);
  const {
    sourceLayer
  } = useGamePMTiles({
    game: usesPMTiles ? game : undefined,
    map,
    mapInitialized: initialized,
    computeFeatureState
  });
  const {
    addGeoJSONSource: addTeamPointsSource,
    updateGeoJSONSource: updateTeamPointsSource
  } = useSource(map, bfgTeamPointsSourceId);
  const loadedIconTeamIds = useRef(new Set());
  const [iconsReadyTick, setIconsReadyTick] = useState(0);
  const [selectedHexProperties, setSelectedHexProperties] = useState(null);

  // Preload every team's icon (skipped for teams without one) so it's ready to draw once the
  // circle markers themselves are built — see the point-building effect below, which re-runs
  // (via iconsReadyTick) once loading settles so already-drawn circles pick up their icon.
  useEffect(() => {
    if (!map || !initialized || !bfgTeams) return;
    let cancelled = false;
    Promise.all(bfgTeams.map(team => loadTeamIcon(map, team).then(() => {
      if (team.icon) loadedIconTeamIds.current.add(team.id);
    }))).then(() => {
      if (!cancelled) setIconsReadyTick(tick => tick + 1);
    }).catch(err => console.error('[ExplorationMapGameIntergroup] team icons', err));
    return () => {
      cancelled = true;
    };
  }, [map, initialized, bfgTeams]);
  useEffect(() => {
    if (game) {
      initMap({
        container: containerId,
        customZoomControls: true,
        scaleControl: false,
        ...getBfgInitialCamera(sites, headquartersGeogroup, mySiteGeogroup)
      });
    }
  }, [game]);
  useEffect(() => {
    const target = document.getElementById('page-layout-content');
    const parent = bleedRootRef.current?.parentElement;
    if (!target || !parent) return;
    function measure() {
      if (!target || !parent) return;
      const targetRect = target.getBoundingClientRect();
      const parentRect = parent.getBoundingClientRect();
      const rootRect = bleedRootRef.current?.getBoundingClientRect();
      setBleedMargins({
        height: rootRect ? window.innerHeight - rootRect.top : 500,
        marginLeft: targetRect.left - parentRect.left,
        width: targetRect.width
      });
    }
    measure();
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(target);
    window.addEventListener('resize', measure);
    // PageLayout's header-image collapse animates over 0.4s and this component can mount at any
    // point during that transition — ResizeObserver tracks it live, but a final re-measure once
    // the animation is guaranteed to be over catches any frame that got missed.
    const settleTimeout = setTimeout(measure, 500);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', measure);
      clearTimeout(settleTimeout);
    };
  }, [game]);

  // bfg_2026 tile styling: fill/border layers plus the per-hex team circle+icon markers.
  useEffect(() => {
    if (!usesPMTiles || !map || !initialized || !sourceLayer) return;
    if (map.getLayer(bfgIntergroupPmtilesLayerId)) return;

    // 'place_other' is the game's own dedicated style's label layer — falls back to appending on
    // top (no anchor) if it's ever missing.
    const beforeId = map.getLayer('place_other') ? 'place_other' : undefined;
    map.addLayer({
      id: bfgIntergroupPmtilesLayerId,
      type: 'fill',
      source: gamePMTilesSourceId,
      'source-layer': sourceLayer,
      minzoom: bfgMinZoom,
      paint: {
        // Sensible values from the very first paint (the mode-aware effect below overwrites
        // these right after) — without this, MapLibre's opaque-black fill/border defaults flash
        // until that effect gets a chance to run.
        'fill-color': buildBfgIntergroupFillColorExpression(bfgTeams ?? [], myTeamId),
        'fill-opacity': buildBfgIntergroupFillOpacityExpression(bfgTeams ?? [], myTeamId),
        'fill-opacity-transition': {
          duration: 150,
          delay: 0
        }
      }
    }, beforeId);
    map.addLayer({
      id: `${bfgIntergroupPmtilesLayerId}-borders`,
      type: 'line',
      source: gamePMTilesSourceId,
      'source-layer': sourceLayer,
      minzoom: bfgMinZoom,
      paint: {
        'line-color': buildBfgIntergroupBorderColorExpression(bfgTeams ?? [], myTeamId),
        'line-width': buildBfgIntergroupBorderWidthExpression(myTeamId)
      }
    }, beforeId);
    if (!map.getLayer(bfgTeamCircleLayerId)) {
      addTeamPointsSource();
      map.addLayer({
        id: bfgTeamCircleLayerId,
        type: 'circle',
        source: bfgTeamPointsSourceId,
        minzoom: bfgMinZoom,
        paint: {
          'circle-color': bfgIntergroupTeamCircleFillColorExpression,
          'circle-radius': bfgIntergroupTeamCircleRadiusExpression,
          'circle-stroke-color': bfgIntergroupTeamCircleStrokeColorExpression,
          'circle-stroke-width': bfgIntergroupTeamCircleStrokeWidthExpression
        }
      });
    }
    if (!map.getLayer(bfgTeamIconLayerId)) {
      map.addLayer({
        id: bfgTeamIconLayerId,
        type: 'symbol',
        source: bfgTeamPointsSourceId,
        minzoom: bfgMinZoom,
        filter: ['has', 'iconId'],
        layout: {
          'icon-image': bfgIntergroupTeamIconImageExpression,
          'icon-size': bfgIntergroupTeamIconSizeExpression,
          'icon-anchor': 'center',
          'symbol-placement': 'point',
          'icon-allow-overlap': true
        }
      });
    }
  }, [usesPMTiles, map, initialized, sourceLayer]);
  useEffect(() => {
    if (!map || !map.getLayer(bfgIntergroupPmtilesLayerId)) return;
    const bordersLayerId = `${bfgIntergroupPmtilesLayerId}-borders`;
    map.setPaintProperty(bfgIntergroupPmtilesLayerId, 'fill-color', buildBfgIntergroupFillColorExpression(bfgTeams ?? [], myTeamId));
    map.setPaintProperty(bfgIntergroupPmtilesLayerId, 'fill-opacity', buildBfgIntergroupFillOpacityExpression(bfgTeams ?? [], myTeamId));
    map.setPaintProperty(bordersLayerId, 'line-color', buildBfgIntergroupBorderColorExpression(bfgTeams ?? [], myTeamId));
    map.setPaintProperty(bordersLayerId, 'line-width', buildBfgIntergroupBorderWidthExpression(myTeamId));
    if (map.getLayer(bfgTeamCircleLayerId)) {
      map.setLayoutProperty(bfgTeamCircleLayerId, 'visibility', 'visible');
    }
    if (map.getLayer(bfgTeamIconLayerId)) {
      map.setLayoutProperty(bfgTeamIconLayerId, 'visibility', 'visible');
    }
  }, [map, initialized, sourceLayer, myTeamId, bfgTeams]);

  // One circle marker per hex (deduplicated by h3 index, placed at its exact center), colored in
  // the leading team's color/icon — only for hexes that actually have a leader.
  useEffect(() => {
    if (!usesPMTiles || !map || !initialized || !sourceLayer || !bfgTeams) return;
    const teams = bfgTeams;
    let refreshTimeout;
    function scheduleRefresh() {
      clearTimeout(refreshTimeout);
      refreshTimeout = setTimeout(refreshTeamPoints, 150);
    }
    function refreshTeamPoints() {
      const features = map.querySourceFeatures(gamePMTilesSourceId, {
        sourceLayer: sourceLayer
      });
      const pointsByH3Index = new globalThis.Map();
      for (const feature of features) {
        const h3Index = feature.properties?.h3_index;
        if (typeof h3Index !== 'string' || pointsByH3Index.has(h3Index)) continue;
        const leader = getBfgLeader(feature.properties, colorMode);
        if (leader.teamId === null || !teams.some(team => team.id === leader.teamId)) continue;
        const teamColor = getBfgIntergroupTeamColor(leader.teamId, teams, myTeamId);
        const hasLoadedIcon = loadedIconTeamIds.current.has(leader.teamId);
        const [lat, lng] = cellToLatLng(h3Index);
        pointsByH3Index.set(h3Index, {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [lng, lat]
          },
          properties: {
            fillColor: teamColor,
            strokeColor: '#FFFFFF',
            strokeWidthFactor: 1,
            ...(hasLoadedIcon ? {
              iconId: teamIconId(leader.teamId)
            } : {})
          }
        });
      }
      updateTeamPointsSource({
        type: 'FeatureCollection',
        features: Array.from(pointsByH3Index.values())
      });
    }
    function handleSourceData(event) {
      if (event.sourceId === gamePMTilesSourceId && event.isSourceLoaded) scheduleRefresh();
    }
    map.on('sourcedata', handleSourceData);
    map.on('moveend', scheduleRefresh);
    refreshTeamPoints();
    return () => {
      clearTimeout(refreshTimeout);
      map.off('sourcedata', handleSourceData);
      map.off('moveend', scheduleRefresh);
    };
  }, [usesPMTiles, map, initialized, sourceLayer, colorMode, bfgTeams, iconsReadyTick, myTeamId]);

  // Hover glow + click, click opens the right-side ranking panel (see selectedHexProperties/JSX
  // below) instead of a map tooltip.
  useEffect(() => {
    if (!usesPMTiles || !map || !initialized || !sourceLayer) return;
    let hoveredId = null;
    function setHover(id, hover) {
      if (id === undefined) return;
      map.setFeatureState({
        source: gamePMTilesSourceId,
        sourceLayer: sourceLayer,
        id
      }, {
        hover
      });
    }
    function handleMouseMove(event) {
      const id = event.features?.[0]?.id;
      if (id === hoveredId) return;
      setHover(hoveredId ?? undefined, false);
      hoveredId = id ?? null;
      setHover(id, true);
      map.getCanvas().style.cursor = 'pointer';
    }
    function handleMouseLeave() {
      setHover(hoveredId ?? undefined, false);
      hoveredId = null;
      map.getCanvas().style.cursor = '';
    }
    function handleClick(event) {
      const feature = event.features?.[0];
      if (!feature) return;
      setSelectedHexProperties(feature.properties);
    }
    map.on('mousemove', bfgIntergroupPmtilesLayerId, handleMouseMove);
    map.on('mouseleave', bfgIntergroupPmtilesLayerId, handleMouseLeave);
    map.on('click', bfgIntergroupPmtilesLayerId, handleClick);
    return () => {
      map.off('mousemove', bfgIntergroupPmtilesLayerId, handleMouseMove);
      map.off('mouseleave', bfgIntergroupPmtilesLayerId, handleMouseLeave);
      map.off('click', bfgIntergroupPmtilesLayerId, handleClick);
    };
  }, [usesPMTiles, map, initialized, sourceLayer]);
  if (!game) return null;
  const ranking = selectedHexProperties ? getBfgIntergroupRanking(selectedHexProperties, bfgTeams ?? [], colorMode) : [];
  const rankingTotal = ranking.reduce((sum, {
    value
  }) => sum + value, 0);
  return /*#__PURE__*/React.createElement(_Box, {
    ref: bleedRootRef,
    sx: {
      marginLeft: bleedMargins ? `${bleedMargins.marginLeft}px` : {
        xs: -2,
        sm: -3,
        md: '-64px'
      },
      width: bleedMargins ? `${bleedMargins.width}px` : undefined
    }
  }, /*#__PURE__*/React.createElement(_Box, {
    overflow: "hidden",
    sx: {
      boxShadow: '0 20px 50px -16px rgba(27,94,32,0.45), 0 4px 16px rgba(0,0,0,0.1)'
    }
  }, /*#__PURE__*/React.createElement(_Box, {
    overflow: "hidden",
    position: "relative",
    sx: {
      height: bleedMargins ? `${bleedMargins.height}px` : 500,
      minHeight: {
        xs: 320,
        sm: 500
      }
    },
    width: "100%"
  }, /*#__PURE__*/React.createElement(_Box, {
    height: "100%",
    id: containerId,
    sx: {
      // Hides the brief flash of the default style before the game's own style has
      // loaded and swapped in (see the setStyle effect above).
      opacity: initialized ? 1 : 0,
      transition: 'opacity 0.15s ease-in'
    },
    width: "100%"
  }), /*#__PURE__*/React.createElement(_Box, {
    bgcolor: "#fff",
    bottom: 0,
    boxShadow: "-8px 0 24px rgba(0,0,0,0.18)",
    display: "flex",
    flexDirection: "column",
    position: "absolute",
    right: 0,
    sx: {
      transform: selectedHexProperties ? 'translateX(0)' : 'translateX(100%)',
      transition: 'transform 200ms ease'
    },
    top: 0,
    width: 260
  }, /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    borderBottom: "1px solid #EEF1EE",
    display: "flex",
    justifyContent: "space-between",
    paddingX: 1.5,
    paddingY: 1
  }, /*#__PURE__*/React.createElement(_Typography, {
    color: "#9AA1AB",
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.4,
    sx: {
      textTransform: 'uppercase'
    }
  }, "Classement passages"), /*#__PURE__*/React.createElement(_IconButton, {
    onClick: () => setSelectedHexProperties(null),
    size: "small"
  }, /*#__PURE__*/React.createElement(_Close, {
    fontSize: "small"
  }))), /*#__PURE__*/React.createElement(_Box, {
    flex: 1,
    overflow: "auto",
    paddingX: 1,
    paddingY: 1
  }, rankingTotal === 0 ? /*#__PURE__*/React.createElement(_Typography, {
    color: "#8A8F98",
    fontSize: 12,
    padding: 1
  }, "Zone non disput\xE9e \u2014 aucun passage enregistr\xE9.") : ranking.map(({
    team,
    value
  }, index) => {
    const isMine = team.id === myTeamId;
    return /*#__PURE__*/React.createElement(_Box, {
      alignItems: "center",
      borderRadius: 2,
      display: "flex",
      gap: 1.125,
      justifyContent: "space-between",
      key: team.id,
      paddingX: 0.75,
      paddingY: 0.625,
      sx: {
        bgcolor: isMine ? `${bfgIntergroupMineColor}1A` : undefined
      }
    }, /*#__PURE__*/React.createElement(_Box, {
      alignItems: "center",
      display: "flex",
      gap: 1.125,
      minWidth: 0
    }, /*#__PURE__*/React.createElement(_Typography, {
      color: "#AEB4BD",
      fontSize: 10,
      fontWeight: 700,
      width: 12
    }, index + 1), team.icon ? /*#__PURE__*/React.createElement(_Box, {
      alt: "",
      component: "img",
      src: team.icon,
      sx: {
        border: '1.5px solid #fff',
        borderRadius: '50%',
        boxShadow: '0 0 0 1px rgba(0,0,0,0.15)',
        flexShrink: 0,
        height: 22,
        objectFit: 'cover',
        width: 22
      }
    }) : /*#__PURE__*/React.createElement(_Box, {
      sx: {
        bgcolor: getBfgIntergroupTeamColor(team.id, bfgTeams ?? [], myTeamId),
        border: '1.5px solid #fff',
        borderRadius: '50%',
        boxShadow: '0 0 0 1px rgba(0,0,0,0.15)',
        flexShrink: 0,
        height: 22,
        width: 22
      }
    }), /*#__PURE__*/React.createElement(_Typography, {
      color: "#1B1D1F",
      fontSize: 12,
      fontWeight: isMine ? 800 : 500,
      sx: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }
    }, team.title ?? 'Équipe', isMine ? ' · Mon entreprise' : '')), /*#__PURE__*/React.createElement(_Typography, {
      color: "#4B5563",
      flexShrink: 0,
      fontSize: 12,
      fontWeight: 700
    }, value));
  }))))));
}
export default ExplorationMapGameIntergroup;